import type { ComponentType, SVGProps } from "react";
import { Bowl, Chilli, Garlic, Herb, Lemon, Tomato, Whisk } from "@/components/landing/FoodMotifs";

type Motif = ComponentType<SVGProps<SVGSVGElement>>;

const VARIANTS: Record<string, { motifs: Motif[]; tints: string[] }> = {
  recipes: {
    motifs: [Whisk, Lemon, Herb],
    tints: ["text-primary/30", "text-mustard/40", "text-secondary/40"],
  },
  menus: {
    motifs: [Bowl, Tomato, Herb],
    tints: ["text-primary/30", "text-tomato/40", "text-secondary/40"],
  },
  shopping: {
    motifs: [Bowl, Garlic, Chilli],
    tints: ["text-primary/30", "text-mustard/40", "text-tomato/40"],
  },
};

type Props = {
  /** Which themed set of food motifs to scatter behind the content. */
  variant?: keyof typeof VARIANTS;
};

/**
 * Decorative scattered food-motif backdrop for empty states. Purely visual —
 * renders the same hand-drawn SVGs used on the landing page so empty
 * collections feel intentional and on-brand instead of barren.
 */
export const EmptyStateMotif = ({ variant = "recipes" }: Props) => {
  const { motifs, tints } = VARIANTS[variant];
  const [A, B, C] = motifs;
  const [tA, tB, tC] = tints;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <A className={`absolute -left-3 top-2 h-14 w-14 -rotate-12 ${tA}`} />
      <B className={`absolute right-2 top-6 h-10 w-10 rotate-6 ${tB}`} />
      <C className={`absolute -bottom-2 left-10 h-12 w-12 rotate-12 ${tC}`} />
    </div>
  );
};
